import React, { useState, useEffect } from 'react';
import { 
  Search, 
  UserPlus, 
  Edit2, 
  Trash2, 
  Check, 
  X,
  AlertCircle,
  Shield,
  Users,
  Calculator,
  Building2,
  RefreshCw,
  Crown,
  Lock,
  UserCheck
} from 'lucide-react';
import { 
  supabase, 
  fetchRoles, 
  fetchCompanies,
  isMagicAdmin,
  getCurrentUserCompany,
  fetchCompanyUsers,
  checkExistingUser,
  testDatabaseConnection, 
  type UserProfile, 
  type Role,
  type Company 
} from '../lib/supabase';

interface DashboardCard {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  requiredFeature?: string;
  requiredRole?: string[];
  onClick?: () => void;
}

const roleIcons = {
  admin: Shield,
  customer_support: Users,
  accountant: Calculator,
  property_manager: Building2
};

export function UserManagement() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isExistingUserModalOpen, setIsExistingUserModalOpen] = useState(false);
  const [existingUserEmail, setExistingUserEmail] = useState('');
  const [existingUserProfile, setExistingUserProfile] = useState<UserProfile | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRoles, setIsLoadingRoles] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'testing' | 'connected' | 'failed'>('testing');
  const [isMagicAdminUser, setIsMagicAdminUser] = useState(false);
  const [currentCompanyId, setCurrentCompanyId] = useState<string | null>(null);
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role_id: '',
    company_id: '',
    company: '',
    department: '',
    password: '' // Only used for new users
  });

  useEffect(() => {
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    // Test database connection first
    const connectionTest = await testDatabaseConnection();
    setConnectionStatus(connectionTest.success ? 'connected' : 'failed');
    
    if (connectionTest.success) {
      await Promise.all([loadData(), loadRoles()]);
    } else {
      setError('Unable to connect to database. Please check your connection.');
    }
  };

  const loadData = async () => {
    try {
      // Get current user's company
      const company = await getCurrentUserCompany();
      if (company) {
        setCurrentCompanyId(company.id);
        setCurrentCompany(company);
        console.log("Current company set:", company.name, company.id);
      }
      
      // Check if user is magic admin
      const magicAdmin = await isMagicAdmin();
      setIsMagicAdminUser(magicAdmin);
      
      // Get current user role
      await getCurrentUserRole();
      
      // Load companies if magic admin
      if (magicAdmin) {
        const companiesData = await fetchCompanies();
        setCompanies(companiesData);
      }
      
      // Fetch users after getting company info
      if (company) {
        await fetchUsers(company.id);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    }
  };

  const getCurrentUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_profiles')
        .select('role:roles(name)')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error fetching user role:', error);
        return;
      }

      if (data && data.role) {
        setCurrentUserRole(data.role.name);
        console.log("Current user role:", data.role.name);
      }
    } catch (err) {
      console.error('Error getting current user role:', err);
    }
  };

  const loadRoles = async () => {
    try {
      setIsLoadingRoles(true);
      setError(null);
      console.log('Starting to load roles...');
      
      const rolesData = await fetchRoles();
      console.log('Roles loaded successfully:', rolesData);
      
      if (rolesData.length === 0) {
        setError('No roles found in database. Please contact an administrator to set up roles.');
      } else {
        setRoles(rolesData);
      }
    } catch (err: any) {
      console.error('Error loading roles:', err);
      setError(`Failed to load roles: ${err.message}`);
    } finally {
      setIsLoadingRoles(false);
    }
  };

  const fetchUsers = async (companyId?: string) => {
    try {
      setIsLoading(true);
      
      if (isMagicAdminUser) {
        // Magic admins can see all users or filter by company
        let query = supabase
          .from('user_profiles')
          .select(`
            *,
            role:roles(name, description),
            company:companies(name)
          `)
          .order('created_at', { ascending: false });
        
        if (companyId) {
          query = query.eq('company_id', companyId);
        }
        
        const { data, error } = await query;
        
        if (error) throw error;
        
        // Process the data to flatten the nested objects
        const processedData = data?.map(user => {
          const processedUser: UserProfile = {
            ...user,
            role_name: user.role?.name || null,
            role_description: user.role?.description || null,
            company_name: user.company?.name || null
          };
          
          // Remove the nested objects to avoid type issues
          delete processedUser.role;
          delete processedUser.company;
          
          return processedUser;
        }) || [];
        
        console.log('Fetched users (magic admin):', processedData.length || 0, 'users');
        setUsers(processedData);
      } else if (companyId) {
        // Regular users can only see users in their company
        console.log("Fetching users for company ID:", companyId);
        const companyUsers = await fetchCompanyUsers(companyId);
        console.log('Fetched company users:', companyUsers.length, 'users');
        setUsers(companyUsers);
      } else {
        console.log('No company ID provided, cannot fetch users');
        setUsers([]);
      }
    } catch (err: any) {
      setError(`Failed to fetch users: ${err.message}`);
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if user has permission
    if (currentUserRole !== 'admin' && !isMagicAdminUser) {
      setError('Only administrators can create users');
      return;
    }
    
    setIsLoading(true);
    setError(null);

    try {
      // Validate required fields
      if (!formData.first_name || !formData.last_name || !formData.email || !formData.password || !formData.role_id) {
        throw new Error('Please fill in all required fields');
      }

      if (formData.password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      // For magic admins, company_id is required
      if (isMagicAdminUser && !formData.company_id) {
        throw new Error('Please select a company');
      }

      // First check if user already exists in our user_profiles table
      const existingProfile = await checkExistingUser(formData.email);
      if (existingProfile) {
        setExistingUserEmail(formData.email);
        setExistingUserProfile(existingProfile);
        setIsExistingUserModalOpen(true);
        setIsLoading(false);
        return;
      }

      // 1. Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password
      });

      if (authError) {
        // Handle specific error for existing user
        if (authError.message === 'User already registered' || authError.code === 'user_already_exists') {
          // Check if this user exists in our profiles but not linked to current company
          const existingProfile = await checkExistingUser(formData.email);
          if (existingProfile) {
            setExistingUserEmail(formData.email);
            setExistingUserProfile(existingProfile);
            setIsExistingUserModalOpen(true);
          } else {
            throw new Error('A user with this email already exists in the authentication system but not in our user profiles. Please contact support for assistance.');
          }
          setIsLoading(false);
          return;
        }
        throw authError;
      }
      if (!authData.user) throw new Error('Failed to create user');

      // 2. Create user profile
      const profileData: any = {
        id: authData.user.id,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        role_id: formData.role_id,
        company: formData.company || 'Default Company',
        department: formData.department || 'General'
      };

      // Add company_id if provided (magic admin feature)
      if (formData.company_id) {
        profileData.company_id = formData.company_id;
        // Get company name for the company field
        const selectedCompany = companies.find(c => c.id === formData.company_id);
        if (selectedCompany) {
          profileData.company = selectedCompany.name;
        }
      } else if (currentCompanyId) {
        // Use current company if not a magic admin
        profileData.company_id = currentCompanyId;
        if (currentCompany) {
          profileData.company = currentCompany.name;
        }
      }

      const { error: profileError } = await supabase
        .from('user_profiles')
        .insert(profileData);

      if (profileError) throw profileError;

      setIsCreateModalOpen(false);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        role_id: '',
        company_id: '',
        company: '',
        department: '',
        password: ''
      });
      fetchUsers(currentCompanyId || undefined);
    } catch (err: any) {
      setError(err.message || 'Failed to create user');
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkExistingUser = async () => {
    if (!existingUserProfile) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const updateData: any = {
        role_id: formData.role_id,
        department: formData.department
      };

      // Add company_id if provided (magic admin feature)
      if (formData.company_id) {
        updateData.company_id = formData.company_id;
        // Get company name for the company field
        const selectedCompany = companies.find(c => c.id === formData.company_id);
        if (selectedCompany) {
          updateData.company = selectedCompany.name;
        }
      } else if (currentCompanyId) {
        // Use current company if not a magic admin
        updateData.company_id = currentCompanyId;
        if (currentCompany) {
          updateData.company = currentCompany.name;
        }
      }

      const { error } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', existingUserProfile.id);

      if (error) throw error;

      setIsExistingUserModalOpen(false);
      setIsCreateModalOpen(false);
      setExistingUserProfile(null);
      setExistingUserEmail('');
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        role_id: '',
        company_id: '',
        company: '',
        department: '',
        password: ''
      });
      fetchUsers(currentCompanyId || undefined);
    } catch (err: any) {
      setError(err.message || 'Failed to link existing user');
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if user has permission
    if (currentUserRole !== 'admin' && !isMagicAdminUser) {
      setError('Only administrators can update users');
      return;
    }
    
    if (!selectedUser) return;
    setIsLoading(true);
    setError(null);

    try {
      const updateData: any = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        role_id: formData.role_id,
        department: formData.department
      };

      // Add company_id if provided (magic admin feature)
      if (formData.company_id) {
        updateData.company_id = formData.company_id;
        // Get company name for the company field
        const selectedCompany = companies.find(c => c.id === formData.company_id);
        if (selectedCompany) {
          updateData.company = selectedCompany.name;
        }
      } else if (!isMagicAdminUser && currentCompanyId) {
        // For non-magic admins, always use current company
        updateData.company_id = currentCompanyId;
        if (currentCompany) {
          updateData.company = currentCompany.name;
        }
      } else if (formData.company) {
        // If company name was manually entered (legacy support)
        updateData.company = formData.company;
      }

      const { error } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', selectedUser.id);

      if (error) throw error;

      setIsEditModalOpen(false);
      fetchUsers(currentCompanyId || undefined);
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    // Check if user has permission
    if (currentUserRole !== 'admin' && !isMagicAdminUser) {
      setError('Only administrators can delete users');
      return;
    }
    
    if (!selectedUser) return;
    setIsLoading(true);
    setError(null);

    try {
      // 1. Delete user profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .delete()
        .eq('id', selectedUser.id);

      if (profileError) throw profileError;

      // Note: In a production environment, you might want to handle auth user deletion differently
      // For now, we'll just delete the profile and let the auth user remain

      setIsDeleteModalOpen(false);
      setSelectedUser(null);
      fetchUsers(currentCompanyId || undefined);
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      role_id: '',
      company_id: '',
      company: '',
      department: '',
      password: ''
    });
  };

  // Check if user is magic admin (gael@jaimy.be or jan@jaimy.be)
  const isMagicAdminEmail = (email: string) => {
    return email === 'gael@jaimy.be' || email === 'jan@jaimy.be';
  };

  const filteredUsers = users.filter(user => {
    // Filter out magic admin users for non-magic admins
    if (!isMagicAdminUser && isMagicAdminEmail(user.email)) {
      return false;
    }
    
    // Apply search filter
    return user.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.department?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.company_name && user.company_name.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  const getRoleIcon = (roleName: string) => {
    const IconComponent = roleIcons[roleName as keyof typeof roleIcons] || Users;
    return IconComponent;
  };

  const retryLoadRoles = async () => {
    await loadRoles();
  };

  // Check if user has permission to manage users
  const canManageUsers = () => {
    return currentUserRole === 'admin' || isMagicAdminUser;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            {isMagicAdminUser && (
              <Crown className="h-6 w-6 text-yellow-500" title="Magic Admin" />
            )}
          </div>
          <p className="text-gray-600 mt-1">
            {isMagicAdminUser 
              ? 'Manage user accounts and permissions across all companies'
              : 'Manage user accounts and permissions'
            }
          </p>
          {!canManageUsers() && (
            <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
              <Lock className="h-4 w-4" />
              You don't have permission to manage users. Contact an administrator for assistance.
            </p>
          )}
          {connectionStatus !== 'connected' && (
            <div className="flex items-center gap-2 mt-2">
              <div className={`w-2 h-2 rounded-full ${
                connectionStatus === 'testing' ? 'bg-yellow-500' :
                connectionStatus === 'failed' ? 'bg-red-500' : 'bg-green-500'
              }`}></div>
              <span className="text-sm text-gray-500">
                Database: {connectionStatus === 'testing' ? 'Connecting...' : 
                          connectionStatus === 'failed' ? 'Connection failed' : 'Connected'}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-64">
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-jaimy-red focus:border-transparent transition-shadow"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <button
            onClick={() => {
              resetForm();
              // Pre-fill company for non-magic admins
              if (!isMagicAdminUser && currentCompanyId && currentCompany) {
                setFormData(prev => ({
                  ...prev,
                  company_id: currentCompanyId,
                  company: currentCompany.name
                }));
              }
              setIsCreateModalOpen(true);
            }}
            disabled={roles.length === 0 || !canManageUsers()}
            className="gradient-bg text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserPlus className="h-5 w-5" />
            <span>Create User</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <div className="flex-1">
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          {error.includes('roles') && (
            <button
              onClick={retryLoadRoles}
              className="text-red-600 hover:text-red-800 flex items-center gap-1"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          )}
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Roles Status */}
      {isLoadingRoles && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-blue-800">Loading roles from database...</p>
        </div>
      )}

      {roles.length === 0 && !isLoadingRoles && !error && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-yellow-600" />
          <div>
            <p className="text-yellow-800 font-medium">No Roles Available</p>
            <p className="text-yellow-700 text-sm">
              No roles found in the database. User creation is disabled until roles are available.
            </p>
          </div>
          <button
            onClick={retryLoadRoles}
            className="text-yellow-600 hover:text-yellow-800 flex items-center gap-1"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Name</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Email</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Role</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Company</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Department</th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="w-6 h-6 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mr-3"></div>
                      Loading users...
                    </div>
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => {
                  const RoleIcon = getRoleIcon(user.role_name || '');
                  const isUserMagicAdmin = isMagicAdminEmail(user.email);
                  
                  return (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-jaimy-red/10 text-jaimy-red flex items-center justify-center font-medium relative">
                            {user.first_name[0]}{user.last_name[0]}
                            {isUserMagicAdmin && (
                              <Crown className="absolute -top-1 -right-1 h-4 w-4 text-yellow-500" />
                            )}
                          </div>
                          <div className="ml-3">
                            <div className="flex items-center gap-2">
                              <div className="text-sm font-medium text-gray-900">
                                {user.first_name} {user.last_name}
                              </div>
                              {isUserMagicAdmin && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <Crown className="h-3 w-3 mr-1" />
                                  Magic Admin
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{user.email}</td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize bg-gray-100 text-gray-800">
                          <RoleIcon className="h-3 w-3 mr-1" />
                          {user.role_name?.replace('_', ' ') || 'No role'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {user.company_name || user.company || 'No company'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{user.department}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right space-x-3">
                        {canManageUsers() ? (
                          <>
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setFormData({
                                  first_name: user.first_name,
                                  last_name: user.last_name,
                                  email: user.email,
                                  role_id: user.role_id || '',
                                  company_id: user.company_id || '',
                                  company: user.company_name || user.company || '',
                                  department: user.department,
                                  password: ''
                                });
                                setIsEditModalOpen(true);
                              }}
                              className="text-gray-400 hover:text-jaimy-red transition-colors"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setIsDeleteModalOpen(true);
                              }}
                              className="text-gray-400 hover:text-red-600 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        ) : (
                          <span className="text-gray-400 flex items-center">
                            <Lock className="h-4 w-4" title="No permission to edit" />
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Create New User</h2>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="p-6 space-y-4">
              {/* Company Selection (Magic Admin Only) */}
              {isMagicAdminUser ? (
                <div>
                  <label htmlFor="company_select" className="block text-sm font-medium text-gray-700">
                    Company *
                  </label>
                  <select
                    id="company_select"
                    required
                    value={formData.company_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_id: e.target.value }))}
                    className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  >
                    <option value="">Select a company</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                /* For non-magic admins, show the current company as read-only */
                currentCompany && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Company
                    </label>
                    <div className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 bg-gray-50 text-gray-700">
                      {currentCompany.name}
                    </div>
                    <input type="hidden" name="company_id" value={currentCompanyId || ''} />
                  </div>
                )
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="first_name"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="last_name"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password *
                </label>
                <input
                  type="password"
                  id="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  placeholder="Minimum 6 characters"
                />
              </div>
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                  Role *
                </label>
                {isLoadingRoles ? (
                  <div className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 bg-gray-50">
                    <div className="flex items-center">
                      <div className="w-4 h-4 border-2 border-jaimy-red border-t-transparent rounded-full animate-spin mr-2"></div>
                      Loading roles...
                    </div>
                  </div>
                ) : roles.length === 0 ? (
                  <div className="mt-1 block w-full rounded-lg border border-red-200 px-4 py-2 bg-red-50">
                    <div className="flex items-center text-red-600">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      No roles available. Please contact an administrator.
                    </div>
                  </div>
                ) : (
                  <select
                    id="role"
                    required
                    value={formData.role_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, role_id: e.target.value }))}
                    className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  >
                    <option value="">Select a role</option>
                    {roles.map((role) => {
                      const RoleIcon = getRoleIcon(role.name);
                      return (
                        <option key={role.id} value={role.id}>
                          {role.name.replace('_', ' ').toUpperCase()} - {role.description.split('.')[0]}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
              <div>
                <label htmlFor="department" className="block text-sm font-medium text-gray-700">
                  Department *
                </label>
                <input
                  type="text"
                  id="department"
                  required
                  value={formData.department}
                  onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || isLoadingRoles || roles.length === 0 || !canManageUsers()}
                  className="gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Create User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Existing User Modal */}
      {isExistingUserModalOpen && existingUserProfile && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">User Already Exists</h2>
              <button
                onClick={() => {
                  setIsExistingUserModalOpen(false);
                  setExistingUserProfile(null);
                  setExistingUserEmail('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                  <UserCheck className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">
                    {existingUserProfile.first_name} {existingUserProfile.last_name}
                  </p>
                  <p className="text-sm text-gray-600">{existingUserEmail}</p>
                </div>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  A user with this email already exists. Current details:
                </p>
                <ul className="mt-2 text-sm text-blue-700 space-y-1">
                  <li><strong>Role:</strong> {existingUserProfile.role_name?.replace('_', ' ') || 'No role'}</li>
                  <li><strong>Company:</strong> {existingUserProfile.company_name || existingUserProfile.company || 'No company'}</li>
                  <li><strong>Department:</strong> {existingUserProfile.department}</li>
                </ul>
              </div>

              <p className="text-gray-600 text-sm mb-6">
                Would you like to update this user's information to match your current company and role settings?
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setIsExistingUserModalOpen(false);
                    setExistingUserProfile(null);
                    setExistingUserEmail('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLinkExistingUser}
                  disabled={isLoading}
                  className="gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <UserCheck className="h-4 w-4" />
                      Update User
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {isEditModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Edit User</h2>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleUpdateUser} className="p-6 space-y-4">
              {/* Company Selection (Magic Admin Only) */}
              {isMagicAdminUser ? (
                <div>
                  <label htmlFor="edit_company_select" className="block text-sm font-medium text-gray-700">
                    Company
                  </label>
                  <select
                    id="edit_company_select"
                    value={formData.company_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_id: e.target.value }))}
                    className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  >
                    <option value="">Select a company</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                /* For non-magic admins, show the current company as read-only */
                currentCompany && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Company
                    </label>
                    <div className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 bg-gray-50 text-gray-700">
                      {currentCompany.name}
                    </div>
                    <input type="hidden" name="company_id" value={currentCompanyId || ''} />
                  </div>
                )
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="edit_first_name" className="block text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="edit_first_name"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                    className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="edit_last_name" className="block text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="edit_last_name"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                    className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="edit_email" className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  id="edit_email"
                  disabled
                  value={formData.email}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 bg-gray-50 text-gray-500 cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              </div>
              <div>
                <label htmlFor="edit_role" className="block text-sm font-medium text-gray-700">
                  Role *
                </label>
                <select
                  id="edit_role"
                  required
                  value={formData.role_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, role_id: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                >
                  <option value="">Select a role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name.replace('_', ' ').toUpperCase()} - {role.description.split('.')[0]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="edit_department" className="block text-sm font-medium text-gray-700">
                  Department *
                </label>
                <input
                  type="text"
                  id="edit_department"
                  required
                  value={formData.department}
                  onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                  className="mt-1 block w-full rounded-lg border border-gray-200 px-4 py-2 focus:ring-2 focus:ring-jaimy-red focus:border-transparent"
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !canManageUsers()}
                  className="gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      Update User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-red-100 text-red-600 mx-auto mb-4 flex items-center justify-center">
                <AlertCircle className="h-6 w-6" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Delete User</h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete {selectedUser.first_name} {selectedUser.last_name}? This action cannot be undone.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={isLoading || !canManageUsers()}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4" />
                      Delete User
                    </>
                  )}
                </button>
              </div>
              {!canManageUsers() && (
                <p className="mt-4 text-sm text-red-500 flex items-center justify-center gap-1">
                  <Lock className="h-4 w-4" />
                  Only administrators can delete users
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}